'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<Theme>('dark');
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => {
            const stored = localStorage.getItem('youraura-theme') as Theme | null;
            setThemeState(stored === 'light' ? 'light' : 'dark');
            setIsHydrated(true);
        });

        return () => window.cancelAnimationFrame(frame);
    }, []);

    useEffect(() => {
        if (!isHydrated) return;

        const root = document.documentElement;
        root.classList.remove('dark', 'light');
        root.classList.add(theme);
        root.style.colorScheme = theme;
        localStorage.setItem('youraura-theme', theme);
    }, [isHydrated, theme]);

    const toggleTheme = () => {
        setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
    };

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
}

export default ThemeProvider;
